import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { EnrolStats } from '../../../model/stats/Stats';
import { StatsService } from '../../../service/stats.service';
import { localizePaymentStatus, PaymentStatus } from '../../../model/Payment';
import {
  SchoolDetailsData,
  SchoolDetailsModalComponent
} from '../../../profile/school-details-modal/school-details-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { LocationService } from '../../../service/location.service';
import { School } from '../../../model/location/School';
import { Region } from '../../../model/location/Region';
import { NotificationHandler } from '../../../helper/notification.handler';
import { MatPaginator } from '@angular/material/paginator';
import { localizeUserStatus, UserStatusType } from '../../../model/auth/User';
import { StatsHelper } from '../../../helper/stats.helper';
import { MobileAwareComponent } from '../../../common/mobile-aware/mobile-aware.component';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
  selector: 'school-enrol-stats',
  templateUrl: './school-enrol-stats.component.html',
  styleUrls: ['./school-enrol-stats.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class SchoolEnrolStatsComponent extends MobileAwareComponent {

  readonly statuses = PaymentStatus;

  @Input() region?: Region;

  @Input() set stats(stats: EnrolStats[] | undefined) {
    if (stats) {
      this.schoolStats = stats;
      this.enrolStats = stats;
    }
  }

  @Input() pageToken?: string;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  schoolStats?: EnrolStats[];
  enrolStats?: EnrolStats[];

  gradeStatsLabels = StatsHelper.gradeChartLabels();

  @Input() set mobile(value: boolean) {
    this.columnsToDisplay = !value
      ? ['action', 'name', 'successful', 'pending', 'scheduled', 'failed', 'invitation_not_accepted']
      : ['name', 'successful', 'failed'];
  }

  @Output() loadNextPage = new EventEmitter<string>();

  columnsToDisplay!: string[];
  expandedElement?: EnrolStats | null;

  showAllSchools = false;
  allSchools?: School[];

  constructor(private statsService: StatsService,
              private locationService: LocationService,
              private dialog: MatDialog,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  localizeStatus(status: PaymentStatus) {
    return localizePaymentStatus(status);
  }

  localizeUserStatus(status: UserStatusType) {
    return localizeUserStatus(status);
  }

  seeSchoolDetails(schoolId: string) {
    if (!schoolId) return;

    const data: SchoolDetailsData = {
      schoolId: schoolId
    };
    this.dialog.open(SchoolDetailsModalComponent, {
      width: '600px',
      data: data
    });
  }

  private loadAllSchoolsData() {
    if (!this.region) return;

    if (this.allSchools) {
      this.fillAllSchoolsData();
      return;
    }

    this.locationService.getSchools(this.region.id).subscribe(schools => {
      this.allSchools = schools;

      this.fillAllSchoolsData();
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Schools data fetch has failed message@@notifications-schools-data-fetch-failed-error:Schools data fetch has failed`, error);
    });
  }

  private fillAllSchoolsData() {
    this.schoolStats = (this.allSchools || [])
      .filter(school => !this.enrolStats || !this.enrolStats.find(s => s.schoolId == school.id))
      .map(school => {
        const stats = new EnrolStats();

        stats.schoolId = school.id;
        stats.schoolName = school.short_name || school.name;

        stats.status_successful = 0;
        stats.status_pending = 0;
        stats.status_scheduled = 0;
        stats.status_failed = 0;

        stats.grade_2 = 0;
        stats.grade_3 = 0;
        stats.grade_4 = 0;
        stats.grade_5 = 0;
        stats.grade_6 = 0;
        stats.grade_7 = 0;
        stats.grade_8 = 0;
        stats.grade_9 = 0;
        stats.grade_10 = 0;
        stats.grade_11 = 0;

        return stats;
      })
      .concat(this.enrolStats || [])
      .sort((s1, s2) => {
        const name1 = s1.schoolName || '';
        const name2 = s2.schoolName || '';

        return name1 > name2 ? 1 : name1 < name2 ? -1 : 0;
      });
  }

  schoolModeChanged() {
    this.showAllSchools = !this.showAllSchools;

    this.schoolStats = undefined;

    if (this.showAllSchools) {
      this.loadAllSchoolsData();
    } else {
      this.schoolStats = this.enrolStats;
    }
  }

  enrolGradeChartData(stats?: EnrolStats): number[] {
    return StatsHelper.gradeChartData(stats);
  }
}
